// Reading a colour never rewrites its saved representation.
const byteHex = value => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, '0');

export function parseColor(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    const raw = value.trim().toLowerCase();
    if (raw === 'transparent') return { hex: '#000000', alpha: 0 };
    if (/^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(raw)) {
        let digits = raw.slice(1);
        if (digits.length < 5) digits = digits.split('').map(char => char + char).join('');
        return { hex: '#' + digits.slice(0, 6), alpha: digits.length === 8 ? parseInt(digits.slice(6), 16) / 255 : 1 };
    }
    if (/^rgba?\(/.test(raw) && raw.endsWith(')')) {
        const channels = raw.slice(raw.indexOf('(') + 1, -1).trim().split(/[\s,/]+/);
        if ((channels.length === 3 || channels.length === 4) &&
            channels.every(channel => /^[-+]?(?:\d*\.?\d+)%?$/.test(channel))) {
            const rgb = channels.slice(0, 3).map(channel => parseFloat(channel) * (channel.endsWith('%') ? 255 / 100 : 1));
            const alpha = channels[3] == null ? 1 : parseFloat(channels[3]) / (channels[3].endsWith('%') ? 100 : 1);
            return { hex: '#' + rgb.map(byteHex).join(''), alpha: Math.max(0, Math.min(1, alpha)) };
        }
    }
    // Let the browser resolve legacy named/HSL colours for display only.
    if (typeof document !== 'undefined') {
        const context = document.createElement('canvas').getContext('2d');
        if (context) {
            context.fillStyle = '#010203';
            context.fillStyle = raw;
            let resolved = context.fillStyle;
            if (resolved === '#010203') {
                context.fillStyle = '#040506';
                context.fillStyle = raw;
                if (context.fillStyle === '#040506') return null;
                resolved = context.fillStyle;
            }
            if (resolved !== raw && (/^#/.test(resolved) || /^rgba?\(/.test(resolved))) return parseColor(resolved);
        }
    }
    return null;
}

export function colorWithAlpha(hex, alpha) {
    const color = parseColor(hex);
    if (!color) return hex;
    const opacity = Math.max(0, Math.min(1, alpha));
    return color.hex + (opacity === 1 ? '' : byteHex(opacity * 255));
}

// Three materials take RGB and opacity separately; saved CSS strings stay intact.
export function getSurfaceColorProps(surface, texture) {
    const color = parseColor(surface.color);
    const alpha = color?.alpha ?? 1;
    const gradientAlpha = (surface.colors || []).some(stop => (parseColor(stop)?.alpha ?? 1) < 1);
    const transparent = texture ? gradientAlpha || alpha < 1 : alpha < 1;
    return {
        color: texture ? '#ffffff' : color?.hex || surface.color,
        opacity: texture ? 1 : alpha,
        transparent,
        depthWrite: !transparent,
    };
}
